import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid2,
  SelectChangeEvent,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { filterObject } from '@src/types/Archivist';
import useDistinctValuesQuery from '@hooks/api/queries/archivist/useArchivistDistinctQuery';

interface DataPointSearchToolbarProps {
  onFiltersChange: (filters: filterObject[]) => void;
  currentFilters: filterObject[];
}

interface DistinctValuesResponse {
  deviceType: (string | null)[];
  parentDeviceId: (string | null)[];
  deviceId: (string | null)[];
  pointName: (string | null)[];
}

const DataPointSearchToolbar: React.FC<DataPointSearchToolbarProps> = ({
  onFiltersChange,
  currentFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<Record<string, string>>({
    deviceType: '',
    parentDeviceId: '',
    deviceId: '',
    pointName: '',
  });

  const { data: distinctValues, isLoading } = useDistinctValuesQuery({
    params: {
      multi_field: ['deviceType', 'parentDeviceId', 'deviceId', 'pointName'],
    },
    options: {
      enabled: true,
    },
  }) as {
    data: DistinctValuesResponse | undefined;
    isLoading: boolean;
  };

  useEffect(() => {
    const filterMap: Record<string, string> = {
      deviceType: '',
      parentDeviceId: '',
      deviceId: '',
      pointName: '',
    };

    currentFilters.forEach((filter) => {
      if (Object.hasOwn(filterMap, filter.field)) {
        filterMap[filter.field] = filter.value;
      }
    });

    setLocalFilters(filterMap);
  }, [currentFilters]);

  const handleFilterChange = useCallback(
    (field: string, value: string) => {
      setLocalFilters((prev) => ({
        ...prev,
        [field]: value,
      }));

      const updatedFilters = currentFilters.filter((f) => f.field !== field);

      if (value && value !== '') {
        updatedFilters.push({ field, value });
      }

      onFiltersChange(updatedFilters);
    },
    [currentFilters, onFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    setLocalFilters({
      deviceType: '',
      parentDeviceId: '',
      deviceId: '',
      pointName: '',
    });
    onFiltersChange([]);
  }, [onFiltersChange]);

  const renderDropdown = (
    field: string,
    label: string,
    options: (string | null)[] | undefined,
  ) => (
    <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
      <FormControl fullWidth size='small' disabled={isLoading}>
        <InputLabel id={`${field}-label`}>{label}</InputLabel>
        <Select
          labelId={`${field}-label`}
          value={localFilters[field]}
          label={label}
          onChange={(e: SelectChangeEvent) =>
            handleFilterChange(field, e.target.value)
          }
        >
          <MenuItem value=''>All {label}</MenuItem>
          {options
            ?.filter((option) => option !== null)
            .map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Grid2>
  );

  return (
    <Box
      sx={{
        p: 3,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Grid2 container spacing={2} sx={{ alignItems: 'center' }}>
        {renderDropdown('deviceType', 'Category', distinctValues?.deviceType)}
        {renderDropdown(
          'parentDeviceId',
          'Parent Device ID',
          distinctValues?.parentDeviceId,
        )}
        {renderDropdown('deviceId', 'Device ID', distinctValues?.deviceId)}
        {renderDropdown('pointName', 'Point Name', distinctValues?.pointName)}
        <Grid2 size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Button
            variant='outlined'
            size='small'
            onClick={clearAllFilters}
            startIcon={<ClearIcon />}
            fullWidth
            sx={{ height: '40px' }}
          >
            Clear Filters
          </Button>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DataPointSearchToolbar;
